import React, { useEffect, useRef, useState } from 'react';
import './PoojaTadwai.css';

const PoojaTadwai = () => {
  const canvasRef = useRef(null);
  const typedTextRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [threatLogs, setThreatLogs] = useState([
    { timestamp: '[2025-01-17 09:23:45]', message: 'Scanning network perimeter...', status: '[SECURE]' },
    { timestamp: '[2025-01-17 09:23:46]', message: 'Firewall rules validated...', status: '[ACTIVE]' },
    { timestamp: '[2025-01-17 09:23:47]', message: 'VPN tunnels encrypted...', status: '[AES-256]' }
  ]);

  // Matrix background effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789ABCDEF';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = [];
    for (let i = 0; i < columns; i++) drops[i] = 1;

    function drawMatrix() {
      ctx.fillStyle = 'rgba(10, 10, 10, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#00ff41';
      ctx.font = fontSize + 'px Share Tech Mono';
      for (let i = 0; i < drops.length; i++) {
        const text = charArray[Math.floor(Math.random() * charArray.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    }

    const interval = setInterval(drawMatrix, 50);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Typing effect
  useEffect(() => {
    const typingTexts = [
      "Defending networks with 99.9% uptime...",
      "Monitoring threats across enterprise infrastructure...",
      "Securing cloud environments with Azure & GCP...",
      "Implementing IAM and access controls...",
      "MS in Cybersecurity @ Wilmington University...",
      "Azure Administrator Associate certified..."
    ];
    let textIndex = 0;
    let charIndex = 0;
    let isDeleting = false;

    function typeText() {
      if (!typedTextRef.current) return;
      const currentText = typingTexts[textIndex];
      if (isDeleting) {
        typedTextRef.current.textContent = currentText.substring(0, charIndex - 1);
        charIndex--;
      } else {
        typedTextRef.current.textContent = currentText.substring(0, charIndex + 1);
        charIndex++;
      }
      let typeSpeed = isDeleting ? 30 : 50;
      if (!isDeleting && charIndex === currentText.length) {
        typeSpeed = 2000;
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        textIndex = (textIndex + 1) % typingTexts.length;
        typeSpeed = 500;
      }
      setTimeout(typeText, typeSpeed);
    }
    typeText();
  }, []);

  // Threat log updates
  useEffect(() => {
    const threats = [
      { action: 'Port scan detected from', ip: '192.168.1.xxx', status: 'BLOCKED' },
      { action: 'Brute force attempt on', ip: 'SSH:22', status: 'BLOCKED' },
      { action: 'Firewall rule updated:', ip: 'ACL-PROD-001', status: 'APPLIED' },
      { action: 'VPN tunnel established:', ip: 'AES-256-GCM', status: 'ENCRYPTED' },
      { action: 'SIEM alert processed:', ip: 'Low severity', status: 'LOGGED' },
      { action: 'Certificate expiry check:', ip: 'TLS 1.3', status: 'VALID' },
      { action: 'User authentication:', ip: 'MFA verified', status: 'APPROVED' },
      { action: 'Patch deployment:', ip: 'CVE-2024-xxxx', status: 'PATCHED' }
    ];

    const interval = setInterval(() => {
      const threat = threats[Math.floor(Math.random() * threats.length)];
      const now = new Date();
      const timestamp = now.toISOString().replace('T', ' ').substring(0, 19);
      setThreatLogs(prev => {
        const newLogs = [...prev, { timestamp: `[${timestamp}]`, message: `${threat.action} ${threat.ip}...`, status: `[${threat.status}]` }];
        return newLogs.slice(-5);
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Smooth scrolling
  useEffect(() => {
    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });

    return () => {
      anchors.forEach(anchor => {
        anchor.removeEventListener('click', () => {});
      });
    };
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const elements = document.querySelectorAll('.experience-item, .cert-card, .skill-category, .stat-card');
    elements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'all 0.6s ease';
      observer.observe(el);
    });

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileMenuOpen && !e.target.closest('.nav')) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  // Cursor trail effect
  useEffect(() => {
    const cursorTrail = [];
    for (let i = 0; i < 10; i++) {
      const dot = document.createElement('div');
      dot.style.cssText = `position:fixed;width:${8 - i * 0.5}px;height:${8 - i * 0.5}px;background:rgba(0,255,65,${1 - i * 0.1});border-radius:50%;pointer-events:none;z-index:9999;`;
      document.body.appendChild(dot);
      cursorTrail.push(dot);
    }

    let mouseX = 0;
    let mouseY = 0;
    const trailPositions = [];

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    document.addEventListener('mousemove', handleMouseMove);

    function animateTrail() {
      trailPositions.unshift({ x: mouseX, y: mouseY });
      if (trailPositions.length > 10) trailPositions.pop();
      cursorTrail.forEach((dot, i) => {
        if (trailPositions[i]) {
          dot.style.left = trailPositions[i].x + 'px';
          dot.style.top = trailPositions[i].y + 'px';
        }
      });
      requestAnimationFrame(animateTrail);
    }
    animateTrail();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      cursorTrail.forEach(dot => dot.remove());
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="matrix-bg" id="matrix"></canvas>
      <div className="grid-bg"></div>
      <div className="scanlines"></div>
      <div className="crt-flicker"></div>
      <div className="noise"></div>

      {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>}
      <nav className="nav">
        <div className="logo">
          <div className="logo-icon">🛡️</div>POOJA<span>.T</span>
        </div>
        <button 
          className="hamburger"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </button>
        <ul className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <li><a href="#home" onClick={() => setIsMobileMenuOpen(false)}>Home</a></li>
          <li><a href="#experience" onClick={() => setIsMobileMenuOpen(false)}>Experience</a></li>
          <li><a href="#skills" onClick={() => setIsMobileMenuOpen(false)}>Skills</a></li>
          <li><a href="#certifications" onClick={() => setIsMobileMenuOpen(false)}>Certifications</a></li>
          <li><a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a></li>
          <li className="mobile-cta-item">
            <a 
              href="#contact" 
              className="mobile-cta-btn"
              onClick={(e) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Establish Connection
            </a>
          </li>
        </ul>
      </nav>

      <section className="hero" id="home">
        <div className="hero-content">
          <div className="hero-text">
            <div className="status-badge"><span className="status-dot"></span>Threat Hunter Active</div>

            {/* Name with Avatar */}
            <div className="name-avatar-section">
              <div className="glitch-wrapper">
                <h1 className="glitch" data-text="POOJA T">POOJA T</h1>
              </div>
              <div className="profile-avatar" id="profile-avatar">
                {/* Placeholder: Female Cybersecurity Bot SVG */}
                <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#0f3460' }} />
                      <stop offset="100%" style={{ stopColor: '#16213e' }} />
                    </linearGradient>
                    <linearGradient id="skinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#e8beac' }} />
                      <stop offset="100%" style={{ stopColor: '#d4a394' }} />
                    </linearGradient>
                    <linearGradient id="hairGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#1a1a2e' }} />
                      <stop offset="100%" style={{ stopColor: '#0d0d1a' }} />
                    </linearGradient>
                  </defs>
                  {/* Background */}
                  <circle cx="60" cy="60" r="58" fill="url(#bgGrad)" />
                  {/* Circuit pattern background */}
                  <path d="M20 30 L40 30 L40 20" stroke="#00ff41" strokeWidth="1" fill="none" opacity="0.3" />
                  <path d="M80 90 L100 90 L100 80" stroke="#00f0ff" strokeWidth="1" fill="none" opacity="0.3" />
                  <circle cx="40" cy="30" r="2" fill="#00ff41" opacity="0.5" />
                  <circle cx="100" cy="90" r="2" fill="#00f0ff" opacity="0.5" />
                  {/* Hair back */}
                  <ellipse cx="60" cy="55" rx="38" ry="42" fill="url(#hairGrad)" />
                  {/* Neck */}
                  <rect x="50" y="85" width="20" height="15" fill="url(#skinGrad)" />
                  {/* Face */}
                  <ellipse cx="60" cy="58" rx="28" ry="32" fill="url(#skinGrad)" />
                  {/* Hair front/bangs */}
                  <path d="M32 45 Q35 25 60 22 Q85 25 88 45 Q85 35 60 32 Q35 35 32 45" fill="url(#hairGrad)" />
                  <path d="M30 55 Q28 40 35 35 L40 50 Z" fill="url(#hairGrad)" />
                  <path d="M90 55 Q92 40 85 35 L80 50 Z" fill="url(#hairGrad)" />
                  {/* Cyber visor/glasses */}
                  <rect x="38" y="50" width="44" height="12" rx="6" fill="#0a0a0a" stroke="#00f0ff" strokeWidth="1.5" />
                  <rect x="40" y="52" width="16" height="8" rx="2" fill="#001a1a" />
                  <rect x="64" y="52" width="16" height="8" rx="2" fill="#001a1a" />
                  {/* Glowing eyes behind visor */}
                  <ellipse cx="48" cy="56" rx="4" ry="3" fill="#00ff41" opacity="0.8" />
                  <ellipse cx="72" cy="56" rx="4" ry="3" fill="#00ff41" opacity="0.8" />
                  {/* Eye glow effect */}
                  <ellipse cx="48" cy="56" rx="2" ry="1.5" fill="#ffffff" opacity="0.6" />
                  <ellipse cx="72" cy="56" rx="2" ry="1.5" fill="#ffffff" opacity="0.6" />
                  {/* Nose */}
                  <path d="M60 60 L58 70 L62 70 Z" fill="#d4a394" opacity="0.5" />
                  {/* Smile */}
                  <path d="M50 76 Q60 82 70 76" stroke="#c49080" strokeWidth="2" fill="none" strokeLinecap="round" />
                  {/* Cyber earpiece */}
                  <ellipse cx="30" cy="58" rx="4" ry="8" fill="#1a1a2e" stroke="#00f0ff" strokeWidth="1" />
                  <circle cx="30" cy="55" r="2" fill="#00ff41" />
                  <ellipse cx="90" cy="58" rx="4" ry="8" fill="#1a1a2e" stroke="#00f0ff" strokeWidth="1" />
                  <circle cx="90" cy="55" r="2" fill="#00ff41" />
                  {/* Shirt/collar hint */}
                  <path d="M40 98 L50 90 L70 90 L80 98 L80 110 L40 110 Z" fill="#1a1a2e" stroke="#00f0ff" strokeWidth="1" />
                  {/* Shield icon on shirt */}
                  <path d="M55 95 L60 93 L65 95 L65 102 L60 105 L55 102 Z" fill="none" stroke="#00ff41" strokeWidth="1.5" />
                  <path d="M58 98 L60 100 L64 96" stroke="#00ff41" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {/* To use your own image, replace the SVG above with: <img src="your-image.jpg" alt="Pooja T" /> */}
              </div>
            </div>

            <p className="subtitle">// CYBERSECURITY ANALYST</p>
            <div className="typing-text"><span ref={typedTextRef} id="typed-text"></span><span className="cursor"></span></div>
            <div className="cta-buttons">
              <a href="#contact" className="btn">Establish Connection</a>
              <a href="#certifications" className="btn btn-secondary">View Credentials</a>
            </div>
          </div>
          <div className="terminal-card">
            <div className="terminal-header">
              <span className="terminal-btn red"></span>
              <span className="terminal-btn yellow"></span>
              <span className="terminal-btn green"></span>
              <span className="terminal-title">pooja@security-ops: ~</span>
            </div>
            <div className="terminal-body">
              <div className="terminal-line"><span className="prompt">$</span> <span className="command">whoami</span></div>
              <div className="terminal-line output">pooja_t_security_analyst</div>
              <div className="terminal-line"><span className="prompt">$</span> <span className="command">cat security_stack.txt</span></div>
              <div className="terminal-line output"><span className="highlight">Network:</span> Cisco Firewalls, VPN, IDS/IPS, ACLs</div>
              <div className="terminal-line output"><span className="highlight">Cloud:</span> Azure, GCP, IAM, RBAC</div>
              <div className="terminal-line output"><span className="highlight">Tools:</span> Wireshark, SNMP, Syslog Analysis</div>
              <div className="terminal-line"><span className="prompt">$</span> <span className="command">echo $CREDENTIALS</span></div>
              <div className="terminal-line output">Azure Administrator Associate ✓</div>
              <div className="terminal-line output">Azure Fundamentals ✓</div>
              <div className="terminal-line"><span className="prompt">$</span> <span className="command">uptime --experience</span></div>
              <div className="terminal-line output">5+ years securing enterprise networks_</div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">99.9%</div>
            <div className="stat-label">Network Uptime</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">45%</div>
            <div className="stat-label">Incidents Reduced</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">35%</div>
            <div className="stat-label">Faster MTTR</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">3.95</div>
            <div className="stat-label">GPA Score</div>
          </div>
        </div>
      </section>

      <section className="section" id="experience">
        <div className="section-header">
          <span className="section-tag">// 01. Career Path</span>
          <h2 className="section-title">SECURITY OPERATIONS</h2>
        </div>
        <div className="experience-timeline">
          <div className="experience-item">
            <div className="exp-header">
              <div>
                <h3 className="exp-title">Network Engineer</h3>
                <p className="exp-company">Symantrix Solutions (Cisco Client), Hyderabad, India</p>
              </div>
              <span className="exp-date">Jan 2021 - Nov 2023</span>
            </div>
            <ul className="exp-description">
              <li>Managed enterprise network infrastructure including routers, switches, firewalls, and VPNs, ensuring <strong>99.9% network uptime</strong></li>
              <li>Configured Cisco routing/switching (VLANs, STP, OSPF, BGP, NAT), reducing incidents by <strong>45%</strong> through proactive monitoring</li>
              <li>Performed daily network monitoring and incident response using SNMP, syslogs, and packet analysis, improving MTTR by <strong>35%</strong></li>
              <li>Implemented network security controls including ACLs, firewall rules, and VPN configurations for secure connectivity</li>
              <li>Created network documentation, topology diagrams, and SOPs supporting ITIL-based change management</li>
            </ul>
          </div>
          <div className="experience-item">
            <div className="exp-header">
              <div>
                <h3 className="exp-title">Associate DevOps Engineer</h3>
                <p className="exp-company">Navitechno Software (Citrix Environment), India</p>
              </div>
              <span className="exp-date">Jul 2018 - Nov 2020</span>
            </div>
            <ul className="exp-description">
              <li>Managed and secured Citrix and cloud environments (Azure, VMware), achieving <strong>99.9% availability</strong> and reducing incidents by <strong>40%</strong></li>
              <li>Automated deployment and patch management using CI/CD pipelines (Jenkins, Git), accelerating releases by <strong>60%</strong></li>
              <li>Led IAM, documentation, and disaster recovery initiatives, achieving <strong>100% DR readiness</strong> and SLA compliance</li>
            </ul>
          </div>
        </div>
        <div className="threat-monitor">
          <div className="threat-header"><span className="pulse"></span>LIVE THREAT MONITOR</div>
          <div className="threat-log">
            {threatLogs.map((log, index) => (
              <div key={index}>
                <span className="timestamp">{log.timestamp}</span> {log.message} <span className="blocked">{log.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="skills">
        <div className="section-header">
          <span className="section-tag">// 02. Arsenal</span>
          <h2 className="section-title">TECHNICAL SKILLS</h2>
        </div>
        <div className="skills-container">
          <div className="skill-category">
            <h3 className="skill-category-title">🔐 Security Operations</h3>
            <div className="skill-tags">
              <span className="skill-tag">Incident Response</span><span className="skill-tag">Packet Analysis</span>
              <span className="skill-tag">Wireshark</span><span className="skill-tag">Log Analysis</span>
              <span className="skill-tag">Syslogs</span><span className="skill-tag">SNMP</span><span className="skill-tag">Root-Cause Analysis</span>
            </div>
          </div>
          <div className="skill-category">
            <h3 className="skill-category-title">🌐 Network Security</h3>
            <div className="skill-tags">
              <span className="skill-tag">Cisco Firewalls</span><span className="skill-tag">VPN (Site-to-Site)</span>
              <span className="skill-tag">VPN (Remote Access)</span><span className="skill-tag">IDS/IPS</span>
              <span className="skill-tag">ACLs</span><span className="skill-tag">VLAN Segmentation</span>
              <span className="skill-tag">OSPF</span><span className="skill-tag">BGP</span>
            </div>
          </div>
          <div className="skill-category">
            <h3 className="skill-category-title">☁️ Cloud & Virtualization</h3>
            <div className="skill-tags">
              <span className="skill-tag">Microsoft Azure</span><span className="skill-tag">Google Cloud (GCP)</span>
              <span className="skill-tag">Azure Active Directory</span><span className="skill-tag">Citrix XenDesktop</span>
              <span className="skill-tag">Citrix XenApp</span><span className="skill-tag">VMware ESXi</span>
            </div>
          </div>
          <div className="skill-category">
            <h3 className="skill-category-title">🔑 Identity & Access</h3>
            <div className="skill-tags">
              <span className="skill-tag">IAM</span><span className="skill-tag">RBAC</span>
              <span className="skill-tag">Azure AD</span><span className="skill-tag">Access Controls</span><span className="skill-tag">DR Planning</span>
            </div>
          </div>
          <div className="skill-category">
            <h3 className="skill-category-title">🖥️ Infrastructure</h3>
            <div className="skill-tags">
              <span className="skill-tag">Cisco Routers</span><span className="skill-tag">Cisco Switches</span>
              <span className="skill-tag">VLAN</span><span className="skill-tag">STP</span><span className="skill-tag">NAT</span>
              <span className="skill-tag">Windows Server</span><span className="skill-tag">Linux (Ubuntu, RHEL)</span>
            </div>
          </div>
          <div className="skill-category">
            <h3 className="skill-category-title">⚙️ DevOps & Automation</h3>
            <div className="skill-tags">
              <span className="skill-tag">Jenkins</span><span className="skill-tag">Git</span>
              <span className="skill-tag">CI/CD Pipelines</span><span className="skill-tag">Docker</span>
              <span className="skill-tag">Python</span><span className="skill-tag">Bash Scripting</span><span className="skill-tag">SQL</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="certifications">
        <div className="section-header">
          <span className="section-tag">// 03. Credentials</span>
          <h2 className="section-title">CERTIFICATIONS</h2>
        </div>
        <div className="certs-grid">
          <div className="cert-card">
            <div className="cert-card-inner">
              <div className="cert-badge">VERIFIED</div>
              <div className="cert-icon">🏆</div>
              <h3 className="cert-title">Azure Administrator Associate</h3>
              <p className="cert-issuer">Microsoft Certified</p>
            </div>
          </div>
          <div className="cert-card">
            <div className="cert-card-inner">
              <div className="cert-badge">VERIFIED</div>
              <div className="cert-icon">☁️</div>
              <h3 className="cert-title">Azure Fundamentals</h3>
              <p className="cert-issuer">Microsoft Certified</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="education">
        <div className="section-header">
          <span className="section-tag">// 04. Education</span>
          <h2 className="section-title">ACADEMIC BACKGROUND</h2>
        </div>
        <div className="education-card">
          <h3 className="edu-degree">Master of Science in Cybersecurity & Information Assurance</h3>
          <p className="edu-school">Wilmington University, Delaware</p>
          <div className="edu-details">
            <div className="edu-detail"><span>GPA:</span> 3.95 / 4.0</div>
            <div className="edu-detail"><span>Duration:</span> Aug 2024 - Dec 2025</div>
            <div className="edu-detail">
              <span>Status:</span>
              <div className="graduated-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Recently Graduated
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div className="contact-content">
          <span className="section-tag">// 05. Secure Channel</span>
          <h2 className="contact-title">ESTABLISH CONNECTION</h2>
          <p className="contact-subtitle">Seeking entry-level Cybersecurity Analyst / SOC Analyst role to apply expertise in network security, cloud security, identity management, and incident response.</p>
          <div className="contact-links">
            <a href="mailto:poojareddypt@gmail.com" className="contact-link">
              <svg viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              poojareddypt@gmail.com
            </a>
            <a href="tel:+14698277732" className="contact-link">
              <svg viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
              +1 (469) 827-7732
            </a>
            <a href="https://www.linkedin.com/in/pooja-tadwai/" target="_blank" rel="noopener noreferrer" className="contact-link">
              <svg viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
              </svg>
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <p className="footer-text"><span>&lt;/&gt;</span> Secured & Built by <span>Pooja T</span> | Protected by <span>vigilance</span> and <span>encryption</span></p>
      </footer>
    </>
  );
};

export default PoojaTadwai;
